import Service from '@root/common/base/Service';
import { ServiceInterface } from '@root/common/types/service';
import { QueryOptions } from '@apollo/client/core';
import { Data as GetTags } from '@root/modules/tags/types/tags';
import getTags from '@root/modules/tags/graphql/getTags.graphql';
import { getPageOffset } from '@root/common/utils/getPageOffset';
import { dataLoader, RedisDataLoaderOptions } from '@root/libs/redis/dataloader/dataLoader';

type ServiceVariables = { limit: number; page?: string; language?: string; search?: Record<string, any> };

type FetchVariables = GetTags['variables'];

type Response = GetTags;

export default class Tags extends Service implements ServiceInterface {
  private fixVariables({ limit = 50, page, language, search }: ServiceVariables) {
    const offset = getPageOffset(limit, page);
    const variables: FetchVariables = {
      offset,
      limit,
      language,
      search,
    };
    return variables;
  }

  private async fetchTags(variables: FetchVariables) {
    try {
      const options = Object.assign({ query: getTags }, { variables });
      const dataLoaderOptions: RedisDataLoaderOptions = {
        remote: {
          keyPrefix: 'tags',
        },
      };
      // Redis dataloader batch function accepts only array in argument
      const requestWrapper = async (options: QueryOptions) => {
        const apiProvider = this.createProvider('GraphQL');
        apiProvider.selectAPI('content-api-v3').setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true });

        const [response] = await apiProvider.query<Response>(options);

        return response.data;
      };

      if (variables.offset !== 0) {
        dataLoaderOptions.remote!.gracePeriodMs = 600000; // 10 minutes for pager
      }

      const redisDataLoader = dataLoader<QueryOptions, Response>(requestWrapper, dataLoaderOptions);
      const response: Response = redisDataLoader ? await redisDataLoader.load(options) : await requestWrapper(options);

      const tags = response.tags;

      return {
        tags,
      };
    } catch (e) {
      const error = this.generateErrorData(e);
      this.handleError(error);
    }
  }

  public async fetch(variables: ServiceVariables) {
    const fixedVariables = this.fixVariables(variables);
    const response = await this.fetchTags(fixedVariables);

    return response;
  }
}

export function fetchTags(variables: ServiceVariables, { req }: { req: PortalRootIncomingMessage }) {
  const tags = new Tags({ req });
  return tags.fetch(variables);
}
